import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios'
import { store } from '@store/index'
import type { TwilioTokenResponse } from '../types/chat.types'

const DEFAULT_BASE_URL = 'http://54.175.123.152:3000'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? DEFAULT_BASE_URL

// Create axios instance for chat API
const chatApi: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: parseInt(import.meta.env.VITE_API_TIMEOUT) || 10000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add authorization token
chatApi.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = store.getState().auth.token

    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
    }

    return config
  },
  error => {
    console.error('[Chat API Request Error]', error)
    return Promise.reject(error)
  }
)

// Response interceptor for error handling
chatApi.interceptors.response.use(
  response => response,
  error => {
    if (axios.isCancel(error) || error?.code === 'ERR_CANCELED') {
      return Promise.reject(error)
    }

    if (error.response) {
      const { status, data } = error.response
      console.error(`[Chat API Error] ${status}:`, data)
    } else if (error.request) {
      console.error('[Chat API Network Error]', error.request)
    } else {
      console.error('[Chat API Error]', error.message)
    }
    return Promise.reject(error)
  }
)

export const therapistTokenEndpoint = (therapistId: string) =>
  `/chat/token/therapist/${therapistId}`

export const patientTokenEndpoint = (patientId: string) =>
  `/chat/token/patient/${patientId}`

export const requestTherapistToken = (
  therapistId: string,
  signal?: AbortSignal
) =>
  chatApi
    .get<TwilioTokenResponse>(therapistTokenEndpoint(therapistId), {
      signal,
    })
    .then(response => response.data)

export const requestPatientToken = (patientId: string, signal?: AbortSignal) =>
  chatApi
    .get<TwilioTokenResponse>(patientTokenEndpoint(patientId), {
      signal,
    })
    .then(response => response.data)

export const agencyTokenEndpoint = (agencyId: string) =>
  `/chat/token/agency/${agencyId}`

export const requestAgencyToken = (agencyId: string, signal?: AbortSignal) =>
  chatApi
    .get<TwilioTokenResponse>(agencyTokenEndpoint(agencyId), {
      signal,
    })
    .then(response => response.data)

export interface CreateConversationPayload {
  therapistId: string
  patientId: string
}

export interface PatientInfo {
  _id: string
  firstName: string
  lastName: string
  profilePhotoUrl?: string
}

export interface TherapistInfo {
  _id: string
  firstName: string
  lastName: string
  profilePhotoUrl?: string
}

export interface ChatConversation {
  agencyId?: any
  _id?: string
  conversationSid?: string // From list endpoints
  conversationId?: string // Primary field from create endpoint (may be missing in list responses)
  reused?: boolean // Indicates if conversation was reused or newly created
  therapistId: string | TherapistInfo // Can be string or populated object (populated for patient view)
  patientId: string | PatientInfo | null // Can be string, populated object, or null (deleted patient)
  therapistUnread?: number
  patientUnread?: number
  createdAt?: string
  updatedAt?: string
  lastMessage?: string
  lastMessageAt?: string
}

export const isTwilioConversationSid = (
  value: string | null | undefined
): value is string => Boolean(value && value.startsWith('CH'))

export const resolveTwilioConversationSid = (
  ...values: Array<string | null | undefined>
): string => {
  const twilioSid = values.find(isTwilioConversationSid)
  if (twilioSid) return twilioSid
  return values.find(value => Boolean(value)) ?? ''
}

export const createConversation = (
  payload: CreateConversationPayload,
  signal?: AbortSignal
) =>
  chatApi
    .post<ChatConversation>('/chat/conversation', payload, {
      signal,
    })
    .then(response => response.data)

// Helper functions to extract IDs from string or object formats
export const extractTherapistId = (
  therapistId: string | TherapistInfo | null | undefined
): string => {
  if (!therapistId) return ''
  return typeof therapistId === 'string' ? therapistId : therapistId._id
}

export const extractPatientId = (
  patientId: string | PatientInfo | null | undefined
): string => {
  if (!patientId) return ''
  return typeof patientId === 'string' ? patientId : patientId._id
}

export interface SendMessagePayload {
  conversationId: string
  senderRole: 'therapist' | 'patient'
  senderId: string // Always a string ID, never an object
  message: string
}

export interface SendMessageResponse {
  sid: string
  conversationId: string
  body: string
  createdAt: string
}

export const sendMessageViaApi = (
  payload: SendMessagePayload,
  signal?: AbortSignal
) =>
  chatApi
    .post<SendMessageResponse>('/chat/message', payload, {
      signal,
    })
    .then(response => response.data)

export const listTherapistConversations = (therapistId: string) =>
  chatApi
    .get<ChatConversation[]>(`/chat/therapist-agency/${therapistId}`)
    .then(response => response.data)

export const listPatientConversations = (patientId: string) =>
  chatApi
    .get<ChatConversation[]>(`/chat/patient-agency/${patientId}`)
    .then(response => response.data)

// Agency-specific endpoints: list conversations scoped to an agency
export const listTherapistConversationsForAgency = (agencyId: string) =>
  chatApi
    .get<ChatConversation[]>(`/chat/agency-therapist/${agencyId}`)
    .then(response => response.data)

export const listPatientConversationsForAgency = (agencyId: string) =>
  chatApi
    .get<ChatConversation[]>(`/chat/agency-patient/${agencyId}`)
    .then(response => response.data)

export interface ChatHistoryMessage {
  _id: string
  chatConversationId: string
  senderRole: 'therapist' | 'patient'
  senderId: string
  message: string
  twilioMessageSid: string
  createdAt: string
  updatedAt: string
  __v?: number
}

export const getChatHistory = (conversationId: string) =>
  chatApi
    .get<ChatHistoryMessage[]>(`/chat/history/${conversationId}`)
    .then(response => response.data)

// Agency-specific conversation creation endpoints
export interface CreateConversationPatientAgencyPayload {
  patientId: string
  agencyId: string
}

export interface CreateConversationTherapistAgencyPayload {
  therapistId: string
  agencyId: string
}

export const createConversationPatientAgency = (
  payload: CreateConversationPatientAgencyPayload,
  signal?: AbortSignal
) =>
  chatApi
    .post<ChatConversation>('/chat/conversation-patient-agency', payload, {
      signal,
    })
    .then(response => response.data)

export const createConversationTherapistAgency = (
  payload: CreateConversationTherapistAgencyPayload,
  signal?: AbortSignal
) =>
  chatApi
    .post<ChatConversation>('/chat/conversation-therapist-agency', payload, {
      signal,
    })
    .then(response => response.data)
